import { TooltipWrapper } from '@components/TooltipWrapper';
import { useAccess } from 'hooks/useAccess';
import { Badge, Button, IconArrowDown, IconArrowUp } from 'uikit';
import { runOn } from 'utils/runOnRefExists';

import cn from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import type { ItemAction } from '../../DropdownContext';
import { DropdownContext } from '../../DropdownContext';
import type { ITableLikeTreeItem } from '../context';
import { TableLikeTreeContext } from '../context';
import { getAllChildren } from '../utils';

import { HierarchyItemInformer } from './HierarchyItemInformer';

import Style from './HierarchyColumnCell.module.css';

interface IHierarchyColumnCell {
    item: ITableLikeTreeItem;
    isContextMenuAvailable?: boolean;
    menuItems?: ItemAction[];
    menuItemsGetter?: (treeItem: ITableLikeTreeItem) => ItemAction[];
}

const setOpacity = (element: any, value: string) => {
    if (element) {
        (element as HTMLDivElement).style.opacity = value;
    }
};

export const HierarchyColumnCell = (props: IHierarchyColumnCell) => {
    const context = useContext(TableLikeTreeContext);
    const { item, isContextMenuAvailable, menuItems, menuItemsGetter } = props;
    const { children, title, id, parent } = item;
    const { hideItems, showItems, shownIds, shownIdsSet, informers, onInformerClose } = context;
    const areChildrenShown = useMemo(() => {
        if (!children) {
            return false;
        }

        if (shownIdsSet) {
            return children.every((child) => shownIdsSet.has(child.id));
        }

        return children.every((child) => shownIds.includes(child.id));
    }, [children, shownIds, shownIdsSet]);
    const allChildrenIds = useMemo(() => getAllChildren(item).map((c) => c.id), [item]);
    const currentInformer = useMemo(() => informers?.find((info) => info.treeItemId === id), [id, informers]);
    const parentInformer = useMemo(
        () => informers?.find((info) => info.treeItemId === parent?.id),
        [informers, parent?.id],
    );
    const ref = useRef<HTMLDivElement>(null);

    const { contractBim: contractBimRules } = useAccess();

    const onClick = useCallback(() => {
        if (areChildrenShown) {
            hideItems(allChildrenIds);
        } else {
            showItems(allChildrenIds);
        }
    }, [allChildrenIds, areChildrenShown, hideItems, showItems]);

    const currentMenuItems = useMemo(() => {
        if (menuItemsGetter) {
            return menuItemsGetter(item);
        }

        return menuItems || [];
    }, [menuItems, menuItemsGetter, item]);

    useEffect(() => {
        if (ref.current && parent) {
            const table = ref.current.closest('.Table');
            const getParent = () => table?.getElementsByClassName(`hierarchy-column-cell-${parent.id}`);
            const parentColumnCell = getParent();

            setOpacity(ref.current, '0');
            setOpacity(parentColumnCell?.[0], '0');

            runOn(
                () => {
                    const parentCell = getParent();

                    return parseInt((parentCell?.[0] as HTMLDivElement)?.style?.marginLeft, 10);
                },
                () => {
                    const parentCell = getParent();

                    if (parentCell?.length) {
                        requestAnimationFrame(() => {
                            setOpacity(ref.current, '1');
                            setOpacity(parentCell?.[0], '1');
                        });
                    }
                },
            );
        }
    }, [parent, parentInformer]);

    const isDisabledDropdown =
        !contractBimRules?.canDelete && !contractBimRules?.canEdit && !contractBimRules?.canCreateContractStructure;

    return (
        <div
            ref={ref}
            className={`${Style.hierarchyColumnCell} hierarchy-column-cell-${id}`}
            style={{ marginLeft: 35 * (item?.treeLevel ?? 0) }}
        >
            {isContextMenuAvailable && !!currentMenuItems?.length && (
                <DropdownContext isDisabled={isDisabledDropdown} items={currentMenuItems} context={{ context, item }} />
            )}

            <Button
                size="xs"
                view="clear"
                onlyIcon
                iconRight={areChildrenShown ? IconArrowUp : IconArrowDown}
                onClick={onClick}
                className={cn({
                    [Style.collapsePlaceholder]: !children?.length,
                })}
            />

            {currentInformer?.items?.length && (
                <div className={Style.hierarchyColumnCellInformerContainer}>
                    {currentInformer?.items.map((informerItem, index) => (
                        <HierarchyItemInformer
                            informer={informerItem}
                            treeItemId={currentInformer.treeItemId}
                            onInformerClose={onInformerClose}
                            key={index}
                        />
                    ))}
                </div>
            )}
            {item.hasWorItems && (
                <TooltipWrapper
                    direction="upCenter"
                    message={
                        item.treeLevel === 0
                            ? 'В составе объекта имеются расценки'
                            : 'В составе подобъекта имеются расценки'
                    }
                >
                    <Badge size="s" label="Р" view="filled" className={Style.hasWorItemsBadge} />
                </TooltipWrapper>
            )}
            <div className="hierarchy-column-cell-title">{title}</div>
        </div>
    );
};

HierarchyColumnCell.displayName = 'HierarchyColumnCell';
